/** Lift systems mailbox. Event, registration, and child-added notes always include it. */
export const SYSTEMS_INBOX = "systems@liftteencenter.com";

export type FamilyNoticeKind = "family_welcome" | "event_confirmation";
export type StaffNoticeKind = "staff_registration" | "staff_rsvp";
export type NoticeKind = FamilyNoticeKind | StaffNoticeKind;

export type ServerDelivery =
  | { action: "send" }
  | { action: "redirect"; to: string }
  | { action: "block" };

export function normalizeAddressList(values: string[]): string[] {
  const seen = new Set<string>();
  const next: string[] = [];
  values.forEach((value) => {
    const email = value.trim().toLowerCase();
    if (!email.includes("@") || email.startsWith("@") || email.endsWith("@")) return;
    if (seen.has(email)) return;
    seen.add(email);
    next.push(email);
  });
  return next;
}

export function environmentName(value: unknown): string {
  if (typeof value !== "string") return "unconfigured";
  return value.trim().toLowerCase().replaceAll('"', "");
}

export function outboundEnabled(value: unknown): boolean {
  return value === true || value === "true";
}

/**
 * Production with outbound email on sends to the real recipients.
 * QA sends only to the server redirect mailbox. Every other case stays recorded.
 */
export function serverMaySend(input: {
  environment: unknown;
  outboundEmail: unknown;
  qaRedirect: string | undefined;
}): ServerDelivery {
  const environment = environmentName(input.environment);
  if (environment === "production" && outboundEnabled(input.outboundEmail)) {
    return { action: "send" };
  }
  if (environment === "qa") {
    const mailbox = normalizeAddressList([input.qaRedirect ?? ""])[0];
    if (mailbox) return { action: "redirect", to: mailbox };
  }
  return { action: "block" };
}

export function familyNoticeRecipients(input: {
  kind: FamilyNoticeKind;
  guardianEmail: string;
  studentEmail: string;
}): string[] {
  if (input.kind === "event_confirmation") {
    return normalizeAddressList([input.guardianEmail]);
  }
  return normalizeAddressList([input.guardianEmail, input.studentEmail]);
}

export function staffNoticeRecipients(input: {
  centralEmail: string;
  usePerSchool: boolean;
  schoolEmail: string;
}): string[] {
  const emails = [SYSTEMS_INBOX, input.centralEmail];
  if (input.usePerSchool) emails.push(input.schoolEmail);
  return normalizeAddressList(emails);
}

export function isNoticeKind(value: unknown): value is NoticeKind {
  return (
    value === "family_welcome" ||
    value === "staff_registration" ||
    value === "event_confirmation" ||
    value === "staff_rsvp"
  );
}
